import React, { useEffect, useState } from "react";
import DrawerLayout from "../..";
import { AddIcon, MergedTicketIcon } from "../../../../icons";
import { message, Tabs } from "antd";
import "./style.scss";
import Checkbox from "../../../checkbox";
import Button from "../../../button";
import SearchableDropdown from "../../../SearchableDropdown";
import { useMergeTickets } from "../../../../hooks/useMergeTickets";

interface Ticket {
  id: number;
  title: string;
  ticketType: string;
  location: string;
  createdBy: string;
  createdOn: string;
  no_of_childs?: string | number;
  is_child?: boolean;
}

interface ServiceDrawerProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  title?: string;
  selectedTickets?: Ticket[];
  allTickets?: Ticket[];
  setSelectedRowKeys?: (keys: React.Key[]) => void;
  setSelectedRows?: (rows: Ticket[]) => void;
}

const MergeTickets: React.FC<ServiceDrawerProps> = ({
  open,
  setOpen,
  title = "Merge tickets",
  selectedTickets = [],
  allTickets = [],
  setSelectedRowKeys,
  setSelectedRows,
}) => {
  const [primaryId, setPrimaryId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [tickets, setTickets] = useState<Ticket[]>(selectedTickets);

  const { mutate: mergeTickets, isPending: isMerging } = useMergeTickets();

  useEffect(() => {
    if (open) {
      setTickets(selectedTickets);
      setSelectedIds(selectedTickets.map((t) => t.id));
      setPrimaryId(null);
    }
  }, [open, selectedTickets]);

  // ✅ Validate if tickets can be merged
  const validateMerge = (): { isValid: boolean; errorMessage?: string } => {
    if (!primaryId) {
      return {
        isValid: false,
        errorMessage: "Please set a primary ticket before merging",
      };
    }

    if (selectedIds.length < 2) {
      return {
        isValid: false,
        errorMessage: "Please select at least 2 tickets to merge",
      };
    }

    const primaryTicket = tickets.find((t) => t.id === primaryId);
    const childTicketIds = selectedIds.filter((id) => id !== primaryId);

    // ✅ Check if primary ticket is already a child ticket
    if (primaryTicket?.is_child) {
      return {
        isValid: false,
        errorMessage:
          "A child ticket cannot be set as primary. Please select a different primary ticket.",
      };
    }

    // ✅ Check if any child ticket is already a parent ticket
    const childTickets = tickets.filter((t) => childTicketIds.includes(t.id));
    const parentTicketAsChild = childTickets.find(
      (t) => t.no_of_childs && parseInt(t.no_of_childs.toString()) > 0
    );

    if (parentTicketAsChild) {
      return {
        isValid: false,
        errorMessage: `Ticket #${parentTicketAsChild.id} is already a parent ticket and cannot be merged as a child.`,
      };
    }

    // ✅ Check if any child ticket is already a child of another ticket
    const alreadyChildTickets = childTickets.filter((t) => t.is_child);
    if (alreadyChildTickets.length > 0) {
      const childTicketNumbers = alreadyChildTickets
        .map((t) => `#${t.id}`)
        .join(", ");
      return {
        isValid: false,
        errorMessage: `Tickets ${childTicketNumbers} are already merged as child tickets and cannot be merged again.`,
      };
    }

    // ✅ Check if trying to merge parent with its own child
    const isMergingParentWithOwnChild = childTickets.some(
      (t) =>
        t.is_child &&
        primaryTicket?.no_of_childs &&
        parseInt(primaryTicket.no_of_childs.toString()) > 0
    );

    if (isMergingParentWithOwnChild) {
      return {
        isValid: false,
        errorMessage:
          "Cannot merge a parent ticket with its own child tickets.",
      };
    }

    return { isValid: true };
  };

  const toggleTicket = (id: number, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((t) => t !== id)
    );
  };

  const toggleDropdown = () => {
    setOpenDropdown(!openDropdown);
  };

  const handleMerge = () => {
    const validation = validateMerge();
    if (!validation.isValid) {
      message.error(validation.errorMessage);
      return;
    }

    // Remove primary ticket from child tickets
    const childTicketIds = selectedIds.filter((id) => id !== primaryId);

    const payload = {
      parent_ticket_id: primaryId!,
      child_ticket_ids: childTicketIds,
    };

    mergeTickets(payload, {
      onSuccess: (response) => {
        message.success(response.message || "Tickets merged successfully");
        setOpen(false);

        // ✅ Clear all selections in the table
        if (setSelectedRowKeys) {
          setSelectedRowKeys([]);
        }
        if (setSelectedRows) {
          setSelectedRows([]);
        }

        // Reset state
        setPrimaryId(null);
        setSelectedIds([]);
        setTickets([]);
      },
      onError: (error: any) => {
        const errorMessage =
          error.response?.data?.message || "Failed to merge tickets";
        message.error(errorMessage);
      },
    });
  };

  const tabItems = tickets.map((ticket) => {
    const informationData = [
      { label: "Ticket type", value: ticket.ticketType },
      { label: "Call type", value: "Dead animal removal" },
      { label: "Location", value: ticket.location },
    ];

    const isChildTicket = ticket.is_child;
    const isParentTicket =
      ticket.no_of_childs && parseInt(ticket.no_of_childs.toString()) > 0;
    const mergeRestriction = isChildTicket
      ? "Already merged as child"
      : isParentTicket
      ? "Parent ticket"
      : null;

    return {
      key: String(ticket.id),
      label: (
        <div className="flex items-start gap-2">
          <Checkbox
            checked={selectedIds.includes(ticket.id)}
            onChange={(e) => toggleTicket(ticket.id, e.target.checked)}
            disabled={isChildTicket}
          />
          <div className="ticket-id-wrapper flex flex-col items-start">
            <span className="ticket-id font-medium">#{ticket.id}</span>
            <span className="ticket-name">{ticket.title}</span>
            {mergeRestriction && (
              <span className="text-xs text-orange-600">
                {mergeRestriction}
              </span>
            )}
          </div>
        </div>
      ),
      children: (
        <div className="p-3 ticket-details-wrapper">
          <div className="detail-header">
            <div className="flex flex-col">
              <p className="ticket-id mb-2">#{ticket.id}</p>
              <h3 className="ticket-name">{ticket.title}</h3>
            </div>
            <div className="creation-details flex">
              <p className="created-date">
                Created on <span>{ticket.createdOn}</span>
              </p>
              <p className="created-by">
                Requested by <span>{ticket.createdBy}</span>
              </p>
            </div>
            <div className="flex gap-2">
              <Checkbox
                checked={primaryId === ticket.id}
                onChange={(e) =>
                  e.target.checked
                    ? setPrimaryId(ticket.id)
                    : setPrimaryId(null)
                }
                disabled={isChildTicket} //Prevent child tickets from being primary
              />
              <span className={isChildTicket ? "text-gray-400" : ""}>
                Set as primary ticket
                {isChildTicket && " (Not allowed for child tickets)"}
              </span>
            </div>

            {(isChildTicket || isParentTicket) && (
              <div className="mt-2 p-2 bg-yellow-50">
                <span className="text-sm text-yellow-800">
                  {isChildTicket
                    ? "This ticket is already merged as a child ticket and cannot be merged again."
                    : `This is a parent ticket with ${ticket.no_of_childs} child ticket(s).`}
                </span>
              </div>
            )}
          </div>

          <div className="ticket-description-wrapper flex flex-col gap-3">
            <span className="description-title">Ticket Description</span>

            <p className="description">
              A discrepancy has been identified in the ticket details for the
              retrofitting of a device on a meter. The recorded information does
              not match the actual installation data, potentially affecting
              system accuracy and operational efficiency.
            </p>

            <div className="additional-details-wrapper">
              <span className="title">Additional details:</span>
              <p className="detail">
                A discrepancy has been identified in the ticket details for the
                retrofitting of a device on a meter.
              </p>
            </div>
          </div>

          <div className="info-details-wrapper flex flex-col gap-4">
            <span className="title">Information</span>
            <div className="content-wrapper flex flex-wrap">
              {informationData.map((item, index) => (
                <div key={index} className="content flex flex-col gap-1">
                  <span className="label">{item.label}</span>
                  <span className="value">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    };
  });

  //  Filter out child tickets from dropdown
  const availableTicketsForDropdown = allTickets
    .filter((t) => !tickets.some((sel) => sel.id === t.id))
    .filter((t) => !t.is_child) //  Exclude already merged child tickets
    .map((t) => ({
      id: t.id,
      label: t.title,
      subLabel: "#" + t.id,
      disabled: t.is_child, //  Show as disabled in dropdown
    }));

  return (
    <DrawerLayout
      className="merge-ticket-drawer"
      width={856}
      open={open}
      setOpen={setOpen}
      title={title}
      extra={<MergedTicketIcon />}
      onConfirm={handleMerge}
      cancelText="Cancel"
      confirmText={isMerging ? "Merging..." : "Merge"}
    >
      <div className="merge-tab-details flex">
        <Tabs
          tabPosition="left"
          items={tabItems}
          className="side-bar-tab w-full"
          tabBarExtraContent={{
            right: (
              <div className="tab-header flex items-center justify-between">
                <span>Selected requests</span>
                <Button
                  onClick={toggleDropdown}
                  className="transparent add-btn"
                  icon={<AddIcon />}
                />
              </div>
            ),
          }}
        />
        <SearchableDropdown
          open={openDropdown}
          onClose={() => setOpenDropdown(false)}
          data={availableTicketsForDropdown}
          placeholder="Search ticket"
          selectedIds={tickets.map((t) => t.id)}
          onToggle={(item, checked) => {
            if (checked) {
              // add to tab list
              const selectedTicket = allTickets.find((t) => t.id === item.id);
              if (selectedTicket)
                setTickets((prev) => [...prev, selectedTicket]);
            } else {
              // remove from tab list
              setTickets((prev) => prev.filter((t) => t.id !== item.id));
            }
          }}
        />
      </div>
    </DrawerLayout>
  );
};

export default MergeTickets;
